import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  input,
  InputSignal,
  OnChanges,
  Signal,
  signal,
  SimpleChanges,
  viewChild,
  viewChildren,
  WritableSignal
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PrimitiveTypes } from '@angular/cli/src/analytics/analytics-parameters';
import { NgClass } from '@angular/common';
import { SkeletonLoaderComponent } from '../../loaders/skeleton-loader/skeleton-loader.component';

export interface FilterListItem {
  id: PrimitiveTypes;
  name: string;
}

@Component({
  selector: 'app-filter-list',
  imports: [NgClass, SkeletonLoaderComponent],
  templateUrl: './filter-list.component.html',
  styleUrl: './filter-list.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FilterListComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterListComponent
  implements ControlValueAccessor, AfterViewInit, OnChanges
{
  filterButtons: Signal<any> = viewChildren<ElementRef>('filterButton');

  filters: InputSignal<FilterListItem[]> = input.required<FilterListItem[]>();
  isLoading: InputSignal<boolean> = input<boolean>(false);
  multi: InputSignal<boolean> = input(false);
  defaultValue: InputSignal<PrimitiveTypes | null> =
    input<PrimitiveTypes | null>(null);
  canDeselect: InputSignal<boolean> = input(true);

  // State
  selectedFilterIds: WritableSignal<PrimitiveTypes[]> = signal<
    PrimitiveTypes[]
  >([]);

  // Check is overflowed
  listElement: Signal<ElementRef> = viewChild.required<ElementRef>('list');
  isOverflowed: WritableSignal<boolean> = signal<boolean>(false);

  isActive(filterId: PrimitiveTypes): boolean {
    const selectedFilterIds: PrimitiveTypes[] = this.selectedFilterIds();
    return selectedFilterIds.some((selectedFilterId: PrimitiveTypes) => {
      return filterId === selectedFilterId;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filters']) {
      this.updateOverflow();
    }
  }

  ngAfterViewInit(): void {
    this.updateOverflow();
  }

  previousFilters() {
    const element = this.listElement().nativeElement as HTMLUListElement;
    element.scrollBy({ left: -200, behavior: 'smooth' });
  }

  nextFilters() {
    const element = this.listElement().nativeElement as HTMLUListElement;
    element.scrollBy({ left: 200, behavior: 'smooth' });
  }

  onFilterClick(id: PrimitiveTypes) {
    const selectedFilterIds: PrimitiveTypes[] = this.selectedFilterIds();
    const isChecked: boolean = selectedFilterIds.some(
      (selectedFilter: PrimitiveTypes): boolean => selectedFilter === id
    );

    if (isChecked) {
      if (!this.canDeselect()) {
        return;
      }

      const defaultValue: PrimitiveTypes | null = this.defaultValue();
      const filteredIds: PrimitiveTypes[] = selectedFilterIds.filter(
        (selectedFilter: PrimitiveTypes): boolean => selectedFilter !== id
      );

      if (filteredIds.length === 0 && defaultValue !== null) {
        filteredIds.push(defaultValue);
      }

      this.selectedFilterIds.set(filteredIds);
      this.onChange?.(filteredIds);
      this.onTouch?.();
      return;
    }

    if (this.multi()) {
      const updatedFilterIds: PrimitiveTypes[] = [...selectedFilterIds, id];
      this.selectedFilterIds.set(updatedFilterIds);
      this.onChange?.(updatedFilterIds);
    } else {
      const updatedFilterIds: PrimitiveTypes[] = [id];
      this.selectedFilterIds.set(updatedFilterIds);
      this.onChange?.(updatedFilterIds[0]);
    }

    this.scrollToActiveFilter();
    this.onTouch?.();
  }

  private updateOverflow() {
    // Wait when view update
    setTimeout(() => {
      const element = this.listElement().nativeElement as HTMLUListElement;
      this.isOverflowed.set(element.offsetWidth < element.scrollWidth);
    });
  }

  private scrollToActiveFilter(): void {
    const selected: PrimitiveTypes = this.selectedFilterIds()[0];

    const button = this.filterButtons().find((ref: any) => {
      return ref.nativeElement.getAttribute('data-id') == selected;
    });

    if (button) {
      button.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }

  // ControlValueAccessor
  private onChange?: (value: PrimitiveTypes | PrimitiveTypes[]) => void;
  onTouch?: () => void;

  registerOnChange(fn: (value: PrimitiveTypes | PrimitiveTypes[]) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  writeValue(rawOutsideValue: PrimitiveTypes | PrimitiveTypes[]): void {
    const outsideValue: PrimitiveTypes[] = Array.isArray(rawOutsideValue)
      ? rawOutsideValue
      : [rawOutsideValue];

    this.selectedFilterIds.set(outsideValue);

    // Scroll to selected filter after view updates
    this.scrollToActiveFilter();
  }
}
